import { registerEdgeType } from '../services/EdgeTypeRegistry';
import { straightEdgeType } from './straightEdgeType';
import type {
  EdgeTypeDefinition, EdgeBoundsContext, EdgeLineBounds,
  TextAnchor, EdgeRoutingContext, WorldPathResult, ExitSide, ExitSides,
} from '../types/EdgeTypes';

const SIDE_ANGLES: Record<ExitSide, number> = {
  right: 0,
  bottom: Math.PI / 2,
  left: Math.PI,
  top: -Math.PI / 2,
};

function _getSide(angle: number): ExitSide {
  const a = Math.atan2(Math.sin(angle), Math.cos(angle));
  const deg45 = Math.PI / 4;

  if (a >= -deg45 && a < deg45) return 'right';
  if (a >= deg45 && a < 3 * deg45) return 'bottom';
  if (a >= -3 * deg45 && a < -deg45) return 'top';
  return 'left';
}

function _isHorizontal(side: ExitSide): boolean {
  return side === 'right' || side === 'left';
}

function getExitSides(
  fromCenterX: number, fromCenterY: number,
  toCenterX: number, toCenterY: number
): ExitSides {
  const angle = Math.atan2(toCenterY - fromCenterY, toCenterX - fromCenterX);
  return {
    exit: _getSide(angle),
    entry: _getSide(angle + Math.PI),
  };
}

function createPath(
  ctx: CanvasRenderingContext2D,
  startX: number, startY: number,
  endX: number, endY: number
): void {
  straightEdgeType.createPath(ctx, startX, startY, endX, endY);
}

function createWorldPathWithContext(
  ctx: CanvasRenderingContext2D,
  fromX: number, fromY: number,
  toX: number, toY: number,
  routing: EdgeRoutingContext
): WorldPathResult {
  const angle = Math.atan2(
    routing.toCenterY - routing.fromCenterY,
    routing.toCenterX - routing.fromCenterX
  );

  const exitSide = _getSide(angle);
  const entrySide = _getSide(angle + Math.PI);

  const exitHorizontal = _isHorizontal(exitSide);
  const entryHorizontal = _isHorizontal(entrySide);

  const path = new Path2D();
  path.moveTo(fromX, fromY);

  ctx.beginPath();
  ctx.moveTo(fromX, fromY);

  if (exitHorizontal && entryHorizontal) {
    const midX = (fromX + toX) / 2;
    path.lineTo(midX, fromY);
    path.lineTo(midX, toY);
    path.lineTo(toX, toY);
    ctx.lineTo(midX, fromY);
    ctx.lineTo(midX, toY);
    ctx.lineTo(toX, toY);
  } else if (exitHorizontal && !entryHorizontal) {
    path.lineTo(toX, fromY);
    path.lineTo(toX, toY);
    ctx.lineTo(toX, fromY);
    ctx.lineTo(toX, toY);
  } else if (!exitHorizontal && entryHorizontal) {
    path.lineTo(fromX, toY);
    path.lineTo(toX, toY);
    ctx.lineTo(fromX, toY);
    ctx.lineTo(toX, toY);
  } else {
    const midY = (fromY + toY) / 2;
    path.lineTo(fromX, midY);
    path.lineTo(toX, midY);
    path.lineTo(toX, toY);
    ctx.lineTo(fromX, midY);
    ctx.lineTo(toX, midY);
    ctx.lineTo(toX, toY);
  }

  return {
    path,
    startTangent: SIDE_ANGLES[exitSide],
    // End tangent = inward travel direction (opposite of entry side's outward direction)
    endTangent: SIDE_ANGLES[entrySide] + Math.PI,
  };
}

function getTangentAtStart(fromX: number, fromY: number, toX: number, toY: number): number {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  return SIDE_ANGLES[_getSide(angle)];
}

function getTangentAtEnd(fromX: number, fromY: number, toX: number, toY: number): number {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  // Return inward travel direction (opposite of entry side's outward direction)
  return SIDE_ANGLES[_getSide(angle + Math.PI)] + Math.PI;
}

function getTextAnchor(fromX: number, fromY: number, toX: number, toY: number): TextAnchor {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  const exitSide = _getSide(angle);
  const entrySide = _getSide(angle + Math.PI);
  const exitH = _isHorizontal(exitSide);
  const entryH = _isHorizontal(entrySide);

  if (exitH && entryH) {
    // horizontal→vertical→horizontal: midpoint of the vertical segment
    const midX = (fromX + toX) / 2;
    return { x: midX, y: (fromY + toY) / 2, angle: 0 };
  } else if (!exitH && !entryH) {
    // vertical→horizontal→vertical: midpoint of the horizontal segment
    const midY = (fromY + toY) / 2;
    return { x: (fromX + toX) / 2, y: midY, angle: 0 };
  } else if (exitH && !entryH) {
    // horizontal→vertical: midpoint is the corner
    return { x: toX, y: fromY, angle: 0 };
  } else {
    // vertical→horizontal: midpoint is the corner
    return { x: fromX, y: toY, angle: 0 };
  }
}

function calculateLineBounds(context: EdgeBoundsContext): EdgeLineBounds {
  return straightEdgeType.calculateLineBounds(context);
}

const stairsEdgeType: EdgeTypeDefinition = {
  value: 'stairs',
  worldSpace: true,
  createPath,
  calculateLineBounds,
  createWorldPathWithContext,
  getExitSides,
  getTangentAtStart,
  getTangentAtEnd,
  getTextAnchor,
};

registerEdgeType(stairsEdgeType);

export { stairsEdgeType };
